import React from 'react';

import type { IntlShape, MessageDescriptor } from 'react-intl-next';

import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import { browser } from '@atlaskit/editor-common/browser';
import type {
	Command,
	CommandDispatch,
	FloatingToolbarButton,
	FloatingToolbarDropdown,
	FloatingToolbarItem,
	Icon,
} from '@atlaskit/editor-common/types';
import { type NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import MagicWandIcon from '@atlaskit/icon/core/magic-wand';
import { RovoIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import sharedMessages from '@atlassian/generative-ai-modal/utils/messages';

import { createOpenAIModalCommand } from '../../../pm-plugins/decoration/actions';
import type {
	AIGlobalOptIn,
	EditorPluginAIProvider,
	SelectionToolbarDropdownConfig,
	SelectionToolbarDropdownOption,
} from '../../../types/types';
import type { EditorPluginAIConfigItem } from '../../prebuilt/config-items/config-items';
import { improveWriting } from '../../prebuilt/config-items/prompts/enhance/enhance';

export const floatingToolbarAIButtonTestID = `ak-editor-ai-selection-toolbar-ai-button`;
export const floatingToolbarAIDropdownTestIDPrefix = `ak-editor-ai-selection-toolbar-dropdown-`;

export const getAIExperienceButton = ({
	intl,
	rangeBaseGenerate,
	hideTitle,
	aiGlobalOptIn,
	icon,
	title,
	tooltip,
	triggeredFor,
	pulse,
	onMount,
	onClick,
	disabled,
}: {
	intl: IntlShape;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	hideTitle?: boolean;
	aiGlobalOptIn: AIGlobalOptIn;
	icon?: Icon;
	title?: MessageDescriptor;
	tooltip?: MessageDescriptor;
	triggeredFor?: NodeType;
	pulse?: boolean;
	onMount?: () => void;
	onClick?: () => void;
	disabled: boolean;
}) => {
	let didMouseEnter = false;

	const metaKey = browser.mac ? '⌘' : 'Ctrl';

	return {
		type: 'button',
		pulse,
		showTitle: true,
		testId: floatingToolbarAIButtonTestID,
		icon: icon ? icon : AtlassianIntelligenceIcon,
		...(!hideTitle && {
			title: intl.formatMessage(title ?? aiMessages.aiToolbarTitle),
		}),
		tooltipContent: intl.formatMessage(tooltip ?? sharedMessages.aiUniversalShortCallToAction, {
			key: metaKey,
		}),
		onMount,
		onMouseEnter: () => {
			didMouseEnter = true;
			return false;
		},
		onClick: (state: EditorState, dispatch?: CommandDispatch, view?: EditorView) => {
			const openAIModalCommand = createOpenAIModalCommand({
				state,
				configItem: rangeBaseGenerate,
				lastTriggeredFrom: 'floatingToolbar',
				// ED-18194 Added analytics for floating toolbar to determine
				// the % of users who click the button vs shift tab to it
				triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
				aiGlobalOptIn,
				triggeredFor,
			});

			openAIModalCommand(state, dispatch);
			if (onClick) {
				onClick();
			}

			return false;
		},
		disabled,
	} as FloatingToolbarButton<Command>;
};

export const convertConfigItemToFloatingToolbarButton = ({
	intl,
	aiGlobalOptIn,
	configItem,
}: {
	intl: IntlShape;
	aiGlobalOptIn: AIGlobalOptIn;
	configItem: EditorPluginAIConfigItem;
}): FloatingToolbarButton<Command> => {
	let didMouseEnter = false;

	return {
		type: 'button',
		showTitle: true,
		testId: `${floatingToolbarAIButtonTestID}-${configItem.key}`,
		icon: () => configItem.icon?.({ formatMessage: intl.formatMessage }),
		title: intl.formatMessage(configItem.title),
		onMouseEnter: () => {
			didMouseEnter = true;
			return false;
		},
		onClick: (state: EditorState, dispatch?: CommandDispatch, view?: EditorView) => {
			const openAIModalCommand = createOpenAIModalCommand({
				state,
				configItem,
				lastTriggeredFrom: 'floatingToolbar',
				triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
				aiGlobalOptIn,
			});

			openAIModalCommand(state, dispatch);
			return false;
		},
	};
};

const getDynamicConfigItem = (state: EditorState) => {
	const text = state.doc.textBetween(state.selection.from, state.selection.to, '\n').trim();
	const wordCount = text.split(/\s+/u).length;

	/**
	 * editor_ai_contextual_selection_toolbar_button
	 * This logic needs to match the AI definitions plugin in
	 * platform/packages/editor/editor-plugin-ai-definitions/src/aiDefinitionsPlugin.tsx
	 *
	 * Otherwise we could have both buttons appear at the same time
	 */
	if (wordCount <= 3) {
		return undefined;
	}

	return improveWriting;
};

export const getDynamicFloatingToolbarButton = ({
	intl,
	aiGlobalOptIn,
	state,
}: {
	intl: IntlShape;
	aiGlobalOptIn: AIGlobalOptIn;
	state: EditorState;
}): FloatingToolbarButton<Command> | undefined => {
	const configItem = getDynamicConfigItem(state);

	if (configItem === undefined) {
		return;
	}

	return convertConfigItemToFloatingToolbarButton({
		intl,
		aiGlobalOptIn,
		configItem,
	});
};

export const getImproveWritingAIButton = ({
	intl,
	hideTitle,
	aiGlobalOptIn,
	disabled,
}: {
	intl: IntlShape;
	hideTitle?: boolean;
	aiGlobalOptIn: AIGlobalOptIn;
	disabled: boolean;
}) => {
	let didMouseEnter = false;

	return {
		type: 'button',
		showTitle: true,
		testId: `${floatingToolbarAIButtonTestID}-improve-writing`,
		icon:
			// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
			fg('ai-smart-button-team-25') || editorExperiment('platform_editor_controls', 'variant1')
				? MagicWandIcon
				: undefined,
		...(!hideTitle && {
			title: intl.formatMessage(aiMessages.improveWritingToolbarIconTitle),
		}),
		tooltipContent: intl.formatMessage(aiMessages.improveWritingToolbarIconTooltip),
		onMouseEnter: () => {
			didMouseEnter = true;
			return false;
		},
		onClick: (state: EditorState, dispatch?: CommandDispatch, view?: EditorView) => {
			const openAIModalCommand = createOpenAIModalCommand({
				state,
				configItem: improveWriting,
				lastTriggeredFrom: 'floatingToolbar',
				triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
				aiGlobalOptIn,
			});

			openAIModalCommand(state, dispatch);
			return false;
		},
		disabled,
	} as FloatingToolbarButton<Command>;
};

const mapSelectionToolbarDropdowns = ({
	selectionToolbarDropdowns,
	intl,
	aiGlobalOptIn,
	disabled,
}: {
	selectionToolbarDropdowns?: SelectionToolbarDropdownConfig[];
	intl: IntlShape;
	aiGlobalOptIn: AIGlobalOptIn;
	disabled: boolean;
}) => {
	if (!selectionToolbarDropdowns) {
		return [];
	}

	return selectionToolbarDropdowns.map((dropdownConfig: SelectionToolbarDropdownConfig) => {
		let title = intl.formatMessage(dropdownConfig.label);
		if (dropdownConfig?.beta) {
			title = `${title} (${intl.formatMessage(sharedMessages.betaLabel)})`;
		}
		return {
			type: 'dropdown',
			testId: floatingToolbarAIDropdownTestIDPrefix + dropdownConfig.id,
			title: title,
			disabled,
			options: dropdownConfig.options.map(
				(option: SelectionToolbarDropdownOption, index: number) => ({
					title: intl.formatMessage(option.label),
					testId: `ak-editor-ai-selection-toolbar-${dropdownConfig.id}-button-${index}`,
					onClick: (state: EditorState, dispatch?: CommandDispatch, view?: EditorView) => {
						const openAIModalCommand = createOpenAIModalCommand({
							state,
							configItem: option.configItem,
							lastTriggeredFrom: 'floatingToolbarDropdown',
							// ED-18194 Added analytics for floating toolbar to determine
							// the % of users who click the button vs shift tab to it
							// triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
							aiGlobalOptIn,
						});

						openAIModalCommand(state, dispatch);
						return false;
					},
				}),
			),
		};
	}) as FloatingToolbarDropdown<Command>[];
};

export const getAIExperienceDropdowns = ({
	intl,
	getSelectionToolbarDropdowns,
	aiGlobalOptIn,
	disabled,
}: {
	intl: IntlShape;
	getSelectionToolbarDropdowns: EditorPluginAIProvider['getSelectionToolbarDropdowns'];
	aiGlobalOptIn: AIGlobalOptIn;
	disabled: boolean;
}) => {
	const selectionToolbarDropdowns = getSelectionToolbarDropdowns?.();
	const aiDropdowns = mapSelectionToolbarDropdowns({
		selectionToolbarDropdowns,
		intl,
		aiGlobalOptIn,
		disabled,
	});

	type DropdownOrSeparator = FloatingToolbarDropdown<Command> | FloatingToolbarItem<Command>;

	const separator: FloatingToolbarItem<Command> = {
		type: 'separator',
	};

	const separatedAIDropdowns = aiDropdowns.reduce<DropdownOrSeparator[]>(
		(acc: DropdownOrSeparator[], val: DropdownOrSeparator) => acc.concat(separator, val),
		[],
	);

	return separatedAIDropdowns;
};

export const getAskAIButton = ({
	intl,
	rangeBaseGenerate,
	aiGlobalOptIn,
	triggeredFor,
	pulse,
	onMount,
	onClick,
	disabled,
}: {
	intl: IntlShape;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	aiGlobalOptIn: AIGlobalOptIn;
	triggeredFor?: NodeType;
	pulse?: boolean;
	onMount?: () => void;
	onClick?: () => void;
	disabled: boolean;
}) => {
	const Icon = () => <RovoIcon size="small"></RovoIcon>;
	return getAIExperienceButton({
		intl,
		rangeBaseGenerate,
		hideTitle: false,
		aiGlobalOptIn,
		icon: Icon,
		title: aiMessages.askAIToolbarIconTitle,
		tooltip: editorExperiment('platform_editor_ai_quickstart_command', true)
			? aiMessages.askAIToolbarIconTooltipQuickCommand
			: aiMessages.askAIToolbarIconTooltip,
		triggeredFor,
		pulse,
		onMount,
		onClick,
		disabled,
	});
};
